import { create } from 'zustand'
import type { Actor } from '../services/moderator-notices'

/**
 * Asks for annotation access, in both directions — the same shape as
 * presenter-requests.store, kept apart because one person can be waiting on
 * both at once and each is answered with its own event.
 *
 *   - `incoming` — what moderators must answer, oldest first. A grant is
 *     `moderate:set_annotator`, a refusal `moderate:deny_annotator`; both reach
 *     every moderator, so whoever did not answer drops it too.
 *   - `myOutcome` — a refusal of MY ask. Approval has no event of its own: the
 *     roles rebroadcast lists me in `annotators`, which the asker reads there.
 */

export type AnnotationRequest = {
  /** SFU peerId of the asker — what `moderate:set_annotator` targets. */
  peerId: string
  name: string
}

type AnnotationRequestsState = {
  incoming: AnnotationRequest[]
  myOutcome: 'rejected' | null
  /** The moderator who refused it, so the notice can name them. */
  myOutcomeBy: Actor | null
  /** Queue an ask. Asking twice is one question. */
  add: (request: AnnotationRequest) => void
  /** Answered (either way), granted elsewhere, or the asker left. */
  remove: (peerId: string) => void
  setMyOutcome: (outcome: 'rejected' | null, by?: Actor | null) => void
  clear: () => void
}

export const useAnnotationRequestsStore = create<AnnotationRequestsState>((set) => ({
  incoming: [],
  myOutcome: null,
  myOutcomeBy: null,
  add: (request) =>
    set((prev) =>
      prev.incoming.some((r) => r.peerId === request.peerId)
        ? prev
        : { incoming: [...prev.incoming, request] },
    ),
  remove: (peerId) =>
    set((prev) =>
      prev.incoming.some((r) => r.peerId === peerId)
        ? { incoming: prev.incoming.filter((r) => r.peerId !== peerId) }
        : prev,
    ),
  setMyOutcome: (myOutcome, by = null) => set({ myOutcome, myOutcomeBy: by }),
  clear: () => set({ incoming: [], myOutcome: null, myOutcomeBy: null }),
}))
